export const notificationPreferenceDefaults = {
  push_enabled: true,
  push_connection_request: true,
  push_connection_accepted: true,
  push_adventure_joined: true,
  push_adventure_interested: true,
  push_direct_message: true,
  push_mention: true,
  push_post_reply: true,
  push_post_like: true,
} as const;

export type NotificationPreferenceKey = keyof typeof notificationPreferenceDefaults;

export type NotificationPreferenceRecord = {
  [K in NotificationPreferenceKey]: boolean;
};

export const notificationPreferenceColumns = Object.keys(
  notificationPreferenceDefaults,
) as NotificationPreferenceKey[];

export const notificationPreferenceItems: Array<{
  key: Exclude<NotificationPreferenceKey, "push_enabled">;
  title: string;
  description: string;
}> = [
  {
    key: "push_direct_message",
    title: "Direct messages",
    description: "Messages from parents you're already talking with.",
  },
  {
    key: "push_mention",
    title: "Mentions",
    description: "When someone includes you in a post, reply, or room chat.",
  },
  {
    key: "push_connection_request",
    title: "Connection requests",
    description: "When another parent would like to connect.",
  },
  {
    key: "push_connection_accepted",
    title: "Accepted connections",
    description: "When someone accepts your connection request.",
  },
  {
    key: "push_adventure_joined",
    title: "Adventure joins",
    description: "When another parent joins an adventure you organised.",
  },
  {
    key: "push_adventure_interested",
    title: "Adventure interest",
    description: "When another parent is interested in an adventure you organised.",
  },
  {
    key: "push_post_reply",
    title: "Replies to your posts",
    description: "When someone replies to something you shared in Kinfeed.",
  },
  {
    key: "push_post_like",
    title: "Likes on your posts",
    description: "When someone likes something you shared in Kinfeed.",
  },
];

const notificationTypeToPreferenceKey: Record<string, NotificationPreferenceKey | null> = {
  connection_request: "push_connection_request",
  connection_accepted: "push_connection_accepted",
  adventure_joined: "push_adventure_joined",
  adventure_interested: "push_adventure_interested",
  direct_message: "push_direct_message",
  mention: "push_mention",
  post_reply: "push_post_reply",
  post_like: "push_post_like",
};

export const getPreferenceKeyForNotificationType = (
  notificationType: string | null | undefined,
): NotificationPreferenceKey | null =>
  notificationType ? notificationTypeToPreferenceKey[notificationType] ?? null : null;

export const buildNotificationPreferenceRecord = (
  value?: Partial<Record<NotificationPreferenceKey, boolean>> | null,
): NotificationPreferenceRecord => ({
  ...notificationPreferenceDefaults,
  ...notificationPreferenceColumns.reduce(
    (acc, key) => {
      if (typeof value?.[key] === "boolean") {
        acc[key] = value[key] as boolean;
      }
      return acc;
    },
    {} as Partial<NotificationPreferenceRecord>,
  ),
});
